<template>
  <div class="content-blocks-editor">
    <!-- Add Block Dropdown -->
    <div class="flex justify-between items-center mb-4">
      <span class="text-xs font-medium text-slate-500 uppercase tracking-wider">
        {{ blocks.length }} {{ blocks.length === 1 ? 'block' : 'blocks' }}
      </span>
      <div class="flex items-center gap-2">
        <button
          v-if="articleId && blocks.length > 0"
          type="button"
          @click="showBulkModal = true"
          class="px-3 py-1.5 border-2 border-slate-400 text-xs font-semibold uppercase tracking-wider bg-white hover:bg-slate-50 transition-colors">
          Bulk Generate Images
        </button>
        <div class="relative">
          <button
            type="button"
            @click="showAddMenu = !showAddMenu"
            class="px-3 py-1.5 border-2 border-neutral-900 text-xs font-semibold uppercase tracking-wider bg-white hover:bg-slate-50 transition-colors">
            + Add Block
          </button>
          <div
            v-if="showAddMenu"
            class="absolute right-0 mt-1 w-48 bg-white border-2 border-neutral-900 shadow-lg z-10">
            <button
              v-for="blockType in blockTypes"
              :key="blockType.type"
              type="button"
              @click="addBlock(blockType.type)"
              class="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 border-b border-slate-200 last:border-b-0">
              {{ blockType.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Blocks List -->
    <div class="space-y-4">
      <div
        v-for="(block, index) in blocks"
        :key="block.id"
        class="iso-card-sm border-2 border-neutral-900 bg-white"
        draggable="true"
        @dragstart="onDragStart($event, index)"
        @dragover.prevent="onDragOver($event, index)"
        @drop="onDrop($event, index)">
        <!-- Block Header -->
        <div class="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200 cursor-move">
          <div class="flex items-center gap-2">
            <span class="text-slate-400">&#x2630;</span>
            <span class="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              {{ getBlockTypeLabel(block.type) }}
            </span>
          </div>
          <div class="flex items-center gap-1">
            <button
              type="button"
              @click="moveBlock(index, -1)"
              :disabled="index === 0"
              class="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
              title="Move up">
              &#x2191;
            </button>
            <button
              type="button"
              @click="moveBlock(index, 1)"
              :disabled="index === blocks.length - 1"
              class="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
              title="Move down">
              &#x2193;
            </button>
            <button
              type="button"
              @click="removeBlock(index)"
              class="p-1 text-red-400 hover:text-red-600"
              title="Delete block">
              &#x2715;
            </button>
          </div>
        </div>

        <!-- Block Image Section (if article is persisted) -->
        <div v-if="articleId" class="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div class="flex items-start gap-3">
            <!-- Image Preview or Placeholder -->
            <div class="flex-shrink-0">
              <template v-if="block.image?.url">
                <img
                  :src="block.image.url"
                  alt="Block image"
                  class="h-16 w-24 object-cover border-2 border-neutral-900">
              </template>
              <template v-else>
                <div class="h-16 w-24 bg-slate-200 border-2 border-dashed border-slate-400 flex items-center justify-center">
                  <svg class="h-6 w-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </template>
            </div>

            <!-- Image Controls -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-2">
                <button
                  type="button"
                  @click="generateBlockImage(index)"
                  :disabled="blockGenerating[index]"
                  class="px-2 py-1 text-xs border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50">
                  <template v-if="blockGenerating[index]">
                    Generating...
                  </template>
                  <template v-else>
                    {{ block.image?.url ? 'Regenerate' : 'Generate' }}
                  </template>
                </button>
                <label class="px-2 py-1 text-xs border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer">
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    @change="uploadBlockImage($event, index)"
                    class="hidden">
                </label>
                <button
                  v-if="block.image?.url"
                  type="button"
                  @click="removeBlockImage(index)"
                  class="px-2 py-1 text-xs border border-red-300 text-red-600 bg-white hover:bg-red-50">
                  Remove
                </button>
                <span v-if="block.image?.cost_cents" class="text-xs text-slate-500">
                  ({{ (block.image.cost_cents / 100).toFixed(2) }})
                </span>
              </div>
              <div v-if="blockStatus[index]" class="text-xs mb-1" :class="blockStatus[index].error ? 'text-red-600' : 'text-slate-500'">
                {{ blockStatus[index].message }}
              </div>
              <input
                v-model="block.imagePrompt"
                @input="emitUpdate"
                type="text"
                class="w-full px-2 py-1 border border-slate-300 text-xs focus:outline-none focus:ring-1 focus:ring-slate-500"
                placeholder="Image prompt (optional - auto-generated if blank)">
            </div>
          </div>
        </div>

        <!-- Block Content Editor -->
        <div class="p-4">
          <!-- Text Block -->
          <template v-if="block.type === 'text'">
            <textarea
              v-model="block.content"
              @input="emitUpdate"
              rows="4"
              class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
              placeholder="Enter text content (supports markdown: **bold**, *italic*, [links](url))" />
          </template>

          <!-- Heading Block -->
          <template v-else-if="block.type === 'heading'">
            <div class="flex gap-2">
              <select
                v-model.number="block.level"
                @change="emitUpdate"
                class="px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500">
                <option :value="2">
                  H2
                </option>
                <option :value="3">
                  H3
                </option>
                <option :value="4">
                  H4
                </option>
              </select>
              <input
                v-model="block.content"
                @input="emitUpdate"
                type="text"
                class="flex-1 px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Heading text">
            </div>
          </template>

          <!-- Image Block -->
          <template v-else-if="block.type === 'image'">
            <div class="space-y-2">
              <input
                v-model="block.url"
                @input="emitUpdate"
                type="url"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Image URL">
              <input
                v-model="block.alt"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Alt text">
              <input
                v-model="block.caption"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Caption (optional)">
            </div>
          </template>

          <!-- Video Block -->
          <template v-else-if="block.type === 'video'">
            <div class="space-y-2">
              <input
                v-model="block.embed_url"
                @input="emitUpdate"
                type="url"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Video embed URL (YouTube, Vimeo)">
              <input
                v-model="block.caption"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Caption (optional)">
            </div>
          </template>

          <!-- FAQ Block -->
          <template v-else-if="block.type === 'faq'">
            <div class="space-y-2">
              <input
                v-model="block.question"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Question">
              <textarea
                v-model="block.answer"
                @input="emitUpdate"
                rows="3"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Answer" />
            </div>
          </template>

          <!-- Callout Block -->
          <template v-else-if="block.type === 'callout'">
            <div class="space-y-2">
              <select
                v-model="block.style"
                @change="emitUpdate"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500">
                <option value="info">
                  Info
                </option>
                <option value="warning">
                  Warning
                </option>
                <option value="tip">
                  Tip
                </option>
                <option value="important">
                  Important
                </option>
              </select>
              <textarea
                v-model="block.content"
                @input="emitUpdate"
                rows="3"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Callout content" />
            </div>
          </template>

          <!-- Quote Block -->
          <template v-else-if="block.type === 'quote'">
            <div class="space-y-2">
              <textarea
                v-model="block.content"
                @input="emitUpdate"
                rows="3"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Quote text" />
              <input
                v-model="block.attribution"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Attribution (optional)">
            </div>
          </template>

          <!-- List Block -->
          <template v-else-if="block.type === 'list'">
            <div class="space-y-2">
              <select
                v-model="block.style"
                @change="emitUpdate"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500">
                <option value="unordered">
                  Bullet List
                </option>
                <option value="ordered">
                  Numbered List
                </option>
              </select>
              <div class="space-y-1">
                <div
                  v-for="(item, itemIndex) in block.items"
                  :key="itemIndex"
                  class="flex gap-2">
                  <input
                    v-model="block.items[itemIndex]"
                    @input="emitUpdate"
                    type="text"
                    class="flex-1 px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                    :placeholder="`Item ${itemIndex + 1}`">
                  <button
                    type="button"
                    @click="removeListItem(index, itemIndex)"
                    class="px-2 text-red-400 hover:text-red-600">
                    &#x2715;
                  </button>
                </div>
                <button
                  type="button"
                  @click="addListItem(index)"
                  class="text-xs text-slate-500 hover:text-slate-700">
                  + Add item
                </button>
              </div>
            </div>
          </template>

          <!-- Code Block -->
          <template v-else-if="block.type === 'code'">
            <div class="space-y-2">
              <input
                v-model="block.language"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Language (e.g., javascript, python)">
              <textarea
                v-model="block.content"
                @input="emitUpdate"
                rows="6"
                class="w-full px-3 py-2 border border-slate-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Code content" />
            </div>
          </template>

          <!-- Table Block -->
          <template v-else-if="block.type === 'table'">
            <div class="space-y-3">
              <!-- Headers -->
              <div>
                <p class="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                  Headers
                </p>
                <div class="flex flex-wrap gap-2">
                  <input
                    v-for="(header, hIndex) in block.headers"
                    :key="'h-' + hIndex"
                    v-model="block.headers[hIndex]"
                    @input="emitUpdate"
                    type="text"
                    class="flex-1 min-w-[120px] px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                    :placeholder="'Header ' + (hIndex + 1)">
                  <button
                    type="button"
                    @click="addTableColumn(index)"
                    class="px-3 py-2 text-xs text-slate-500 hover:text-slate-700 border border-dashed border-slate-300">
                    + Column
                  </button>
                </div>
              </div>
              <!-- Rows -->
              <div>
                <p class="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                  Rows
                </p>
                <div class="space-y-2">
                  <div
                    v-for="(row, rIndex) in block.rows"
                    :key="'r-' + rIndex"
                    class="flex gap-2">
                    <input
                      v-for="(cell, cIndex) in row"
                      :key="'c-' + cIndex"
                      v-model="block.rows[rIndex][cIndex]"
                      @input="emitUpdate"
                      type="text"
                      class="flex-1 min-w-[120px] px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                      :placeholder="block.headers[cIndex] || 'Cell'">
                    <button
                      type="button"
                      @click="removeTableRow(index, rIndex)"
                      class="px-2 text-red-400 hover:text-red-600">
                      &#x2715;
                    </button>
                  </div>
                  <button
                    type="button"
                    @click="addTableRow(index)"
                    class="text-xs text-slate-500 hover:text-slate-700">
                    + Add row
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="blocks.length === 0"
      class="text-center py-12 border-2 border-dashed border-slate-300 rounded">
      <p class="text-slate-500 mb-4">
        No content blocks yet
      </p>
      <button
        type="button"
        @click="showAddMenu = true"
        class="px-4 py-2 border-2 border-neutral-900 text-xs font-semibold uppercase tracking-wider bg-white hover:bg-slate-50 transition-colors">
        Add Your First Block
      </button>
    </div>

    <!-- Bulk Generation Modal -->
    <div
      v-if="showBulkModal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="bulk-modal-title"
      role="dialog"
      aria-modal="true">
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div
          class="fixed inset-0 bg-slate-500 bg-opacity-75 transition-opacity"
          @click="closeBulkModal" />

        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-white border-2 border-neutral-900 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <!-- Header -->
          <div class="px-6 py-4 bg-slate-50 border-b-2 border-neutral-900">
            <h3 id="bulk-modal-title" class="text-sm font-semibold text-slate-900 uppercase tracking-wider">
              Bulk Generate Images
            </h3>
          </div>

          <!-- Content -->
          <div class="px-6 py-4">
            <p class="text-sm text-slate-600 mb-4">
              Generate images for all content blocks that don't have one yet.
              Each image costs $0.04 to generate.
            </p>

            <!-- Options -->
            <div class="space-y-3 mb-4">
              <label class="flex items-center gap-2">
                <input
                  v-model="bulkOptions.includeCover"
                  type="checkbox"
                  class="rounded border-slate-300 text-slate-900 focus:ring-slate-500">
                <span class="text-sm text-slate-700">Include cover image</span>
              </label>
              <label class="flex items-center gap-2">
                <input
                  v-model="bulkOptions.includeBlocks"
                  type="checkbox"
                  class="rounded border-slate-300 text-slate-900 focus:ring-slate-500">
                <span class="text-sm text-slate-700">Include block images ({{ blocksWithoutImages }} without images)</span>
              </label>
              <label class="flex items-center gap-2">
                <input
                  v-model="bulkOptions.forceRegenerate"
                  type="checkbox"
                  class="rounded border-slate-300 text-slate-900 focus:ring-slate-500">
                <span class="text-sm text-slate-700">Regenerate existing images</span>
              </label>
            </div>

            <!-- Estimated Cost -->
            <div class="p-3 bg-slate-100 border border-slate-200 mb-4">
              <div class="flex justify-between text-sm">
                <span class="text-slate-600">Estimated cost:</span>
                <span class="font-semibold text-slate-900">{{ estimatedCost }}</span>
              </div>
            </div>

            <!-- Progress -->
            <div v-if="bulkGenerating" class="mb-4">
              <div class="flex justify-between text-xs text-slate-500 mb-1">
                <span>{{ bulkProgress.message }}</span>
                <span>{{ bulkProgress.current }}/{{ bulkProgress.total }}</span>
              </div>
              <div class="w-full h-2 bg-slate-200 border border-slate-300">
                <div
                  class="h-full bg-slate-700 transition-all"
                  :style="{ width: bulkProgress.percent + '%' }" />
              </div>
            </div>

            <!-- Result -->
            <div v-if="bulkResult" class="p-3 border" :class="bulkResult.success ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'">
              <p class="text-sm" :class="bulkResult.success ? 'text-green-800' : 'text-red-800'">
                {{ bulkResult.message }}
              </p>
              <div v-if="bulkResult.errors && bulkResult.errors.length > 0" class="mt-2 text-xs text-red-600">
                <p v-for="(err, i) in bulkResult.errors" :key="i">
                  {{ err }}
                </p>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
            <button
              type="button"
              @click="closeBulkModal"
              :disabled="bulkGenerating"
              class="px-4 py-2 text-xs font-semibold uppercase tracking-wider border-2 border-slate-400 bg-white hover:bg-slate-50 disabled:opacity-50">
              {{ bulkResult ? 'Close' : 'Cancel' }}
            </button>
            <button
              v-if="!bulkResult"
              type="button"
              @click="runBulkGeneration"
              :disabled="bulkGenerating || (!bulkOptions.includeCover && !bulkOptions.includeBlocks)"
              class="px-4 py-2 text-xs font-semibold uppercase tracking-wider border-2 border-neutral-900 bg-white hover:bg-slate-50 disabled:opacity-50">
              <template v-if="bulkGenerating">
                Generating...
              </template>
              <template v-else>
                Generate Images
              </template>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue'

export default {
  name: 'ContentBlocksEditor',

  props: {
    initialBlocks: {
      type: Array,
      default: () => []
    },
    onUpdate: {
      type: Function,
      required: true
    },
    articleId: {
      type: String,
      default: null
    },
    generateBlockUrl: {
      type: String,
      default: null
    },
    uploadBlockUrl: {
      type: String,
      default: null
    },
    generateAllUrl: {
      type: String,
      default: null
    }
  },

  setup(props) {
    // Normalize blocks to handle legacy schema formats
    const normalizeBlock = (block) => {
      const normalized = { ...block }

      // Heading: convert string level ("h2") to integer (2)
      if (block.type === 'heading' && typeof block.level === 'string') {
        const match = block.level.match(/h(\d)/)
        normalized.level = match ? parseInt(match[1], 10) : 2
      }

      // Video: migrate 'url' to 'embed_url'
      if (block.type === 'video' && block.url && !block.embed_url) {
        normalized.embed_url = block.url
        delete normalized.url
      }

      // Table: migrate CSV 'content' to headers/rows arrays
      if (block.type === 'table' && block.content && !block.headers) {
        const lines = block.content.split('\n').filter((l) => l.trim())
        if (lines.length > 0) {
          normalized.headers = lines[0].split(',').map((h) => h.trim())
          normalized.rows = lines.slice(1).map((line) => line.split(',').map((c) => c.trim()))
        } else {
          normalized.headers = ['Column 1', 'Column 2']
          normalized.rows = [['', '']]
        }
        delete normalized.content
      } else if (block.type === 'table' && !block.headers) {
        normalized.headers = ['Column 1', 'Column 2']
        normalized.rows = [['', '']]
      }

      return normalized
    }

    const blocks = ref(props.initialBlocks.map((b, i) => ({
      ...normalizeBlock(b),
      id: b.id || `block-${i}-${Date.now()}`
    })))
    const showAddMenu = ref(false)
    const dragIndex = ref(null)

    const blockTypes = [
      { type: 'text', label: 'Text' },
      { type: 'heading', label: 'Heading' },
      { type: 'image', label: 'Image' },
      { type: 'video', label: 'Video' },
      { type: 'faq', label: 'FAQ' },
      { type: 'callout', label: 'Callout' },
      { type: 'quote', label: 'Quote' },
      { type: 'list', label: 'List' },
      { type: 'code', label: 'Code' },
      { type: 'table', label: 'Table' }
    ]

    const getBlockTypeLabel = (type) => {
      const found = blockTypes.find((bt) => bt.type === type)
      return found ? found.label : type
    }

    const emitUpdate = () => {
      // Remove the internal id before sending to parent
      const cleanBlocks = blocks.value.map(({ id: _id, ...rest }) => rest)
      props.onUpdate(cleanBlocks)
    }

    const addBlock = (type) => {
      const newBlock = { id: `block-${Date.now()}`, type }

      switch (type) {
        case 'text':
          newBlock.content = ''
          break
        case 'heading':
          newBlock.level = 2 // Integer, not string
          newBlock.content = ''
          break
        case 'image':
          newBlock.url = ''
          newBlock.alt = ''
          newBlock.caption = ''
          break
        case 'video':
          newBlock.embed_url = '' // Match renderer's expected field name
          newBlock.caption = ''
          break
        case 'faq':
          newBlock.question = ''
          newBlock.answer = ''
          break
        case 'callout':
          newBlock.style = 'info'
          newBlock.content = ''
          break
        case 'quote':
          newBlock.content = ''
          newBlock.attribution = ''
          break
        case 'list':
          newBlock.style = 'unordered'
          newBlock.items = ['']
          break
        case 'code':
          newBlock.language = ''
          newBlock.content = ''
          break
        case 'table':
          newBlock.headers = ['Column 1', 'Column 2'] // Array of headers
          newBlock.rows = [['', '']] // 2D array of rows
          break
      }

      blocks.value.push(newBlock)
      showAddMenu.value = false
      emitUpdate()
    }

    const removeBlock = (index) => {
      if (confirm('Are you sure you want to delete this block?')) {
        blocks.value.splice(index, 1)
        emitUpdate()
      }
    }

    const moveBlock = (index, direction) => {
      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= blocks.value.length) return

      const block = blocks.value.splice(index, 1)[0]
      blocks.value.splice(newIndex, 0, block)
      emitUpdate()
    }

    const addListItem = (blockIndex) => {
      blocks.value[blockIndex].items.push('')
      emitUpdate()
    }

    const removeListItem = (blockIndex, itemIndex) => {
      blocks.value[blockIndex].items.splice(itemIndex, 1)
      emitUpdate()
    }

    // Table helpers
    const addTableColumn = (blockIndex) => {
      const block = blocks.value[blockIndex]
      block.headers.push('')
      // Add empty cell to each existing row
      block.rows.forEach((row) => row.push(''))
      emitUpdate()
    }

    const addTableRow = (blockIndex) => {
      const block = blocks.value[blockIndex]
      const colCount = block.headers.length
      block.rows.push(Array(colCount).fill(''))
      emitUpdate()
    }

    const removeTableRow = (blockIndex, rowIndex) => {
      blocks.value[blockIndex].rows.splice(rowIndex, 1)
      emitUpdate()
    }

    // Drag and drop handlers
    const onDragStart = (event, index) => {
      dragIndex.value = index
      event.dataTransfer.effectAllowed = 'move'
    }

    const onDragOver = (event, _index) => {
      event.dataTransfer.dropEffect = 'move'
    }

    const onDrop = (event, index) => {
      if (dragIndex.value === null || dragIndex.value === index) return

      const block = blocks.value.splice(dragIndex.value, 1)[0]
      blocks.value.splice(index, 0, block)
      dragIndex.value = null
      emitUpdate()
    }

    // Image generation state
    const blockGenerating = reactive({})
    const blockStatus = reactive({})
    const showBulkModal = ref(false)

    // Bulk generation state
    const bulkOptions = reactive({
      includeCover: true,
      includeBlocks: true,
      forceRegenerate: false
    })
    const bulkGenerating = ref(false)
    const bulkProgress = reactive({
      current: 0,
      total: 0,
      percent: 0,
      message: ''
    })
    const bulkResult = ref(null)

    const getCSRFToken = () => {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute('content') : ''
    }

    const generateBlockImage = async(index) => {
      if (!props.generateBlockUrl || blockGenerating[index]) return

      const block = blocks.value[index]
      const prompt = block.imagePrompt || null
      const force = !!block.image?.url

      blockGenerating[index] = true
      blockStatus[index] = { message: 'Generating image...', error: false }

      try {
        const response = await fetch(props.generateBlockUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken()
          },
          body: JSON.stringify({ block_index: index, prompt, force })
        })

        const data = await response.json()

        if (data.success) {
          if (data.skipped) {
            blockStatus[index] = { message: 'Image already exists', error: false }
          } else {
            block.image = {
              url: data.url,
              prompt: data.prompt,
              cost_cents: data.cost_cents,
              generated_at: new Date().toISOString()
            }
            blockStatus[index] = { message: 'Generated!', error: false }
            emitUpdate()
          }
        } else {
          blockStatus[index] = { message: data.message || data.error, error: true }
        }
      } catch (error) {
        blockStatus[index] = { message: error.message, error: true }
      } finally {
        blockGenerating[index] = false
        setTimeout(() => { blockStatus[index] = null }, 3000)
      }
    }

    const uploadBlockImage = async(event, index) => {
      if (!props.uploadBlockUrl) return

      const file = event.target.files?.[0]
      if (!file) return

      const block = blocks.value[index]
      blockStatus[index] = { message: 'Uploading...', error: false }

      const formData = new FormData()
      formData.append('block_index', index)
      formData.append('image', file)

      try {
        const response = await fetch(props.uploadBlockUrl, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': getCSRFToken()
          },
          body: formData
        })

        const data = await response.json()

        if (data.success) {
          block.image = {
            url: data.url,
            prompt: block.imagePrompt || null,
            cost_cents: 0,
            generated_at: null
          }
          blockStatus[index] = { message: 'Uploaded!', error: false }
          emitUpdate()
        } else {
          blockStatus[index] = { message: data.message || data.error, error: true }
        }
      } catch (error) {
        blockStatus[index] = { message: error.message, error: true }
      } finally {
        event.target.value = ''
        setTimeout(() => { blockStatus[index] = null }, 3000)
      }
    }

    const removeBlockImage = (index) => {
      if (!confirm('Remove this image?')) return

      const block = blocks.value[index]
      delete block.image
      emitUpdate()
    }

    // Bulk generation computed properties
    const blocksWithoutImages = computed(() => blocks.value.filter((b) => !b.image?.url).length)

    const estimatedCost = computed(() => {
      let count = 0
      if (bulkOptions.includeCover) {
        count += 1 // Cover image
      }
      if (bulkOptions.includeBlocks) {
        if (bulkOptions.forceRegenerate) {
          count += blocks.value.length
        } else {
          count += blocksWithoutImages.value
        }
      }
      const dollars = (count * 4) / 100
      return `$${dollars.toFixed(2)} (${count} image${count !== 1 ? 's' : ''})`
    })

    const closeBulkModal = () => {
      if (bulkGenerating.value) return
      showBulkModal.value = false
      bulkResult.value = null
      bulkProgress.current = 0
      bulkProgress.total = 0
      bulkProgress.percent = 0
      bulkProgress.message = ''
    }

    const runBulkGeneration = async() => {
      if (!props.generateAllUrl || bulkGenerating.value) return

      bulkGenerating.value = true
      bulkResult.value = null
      bulkProgress.current = 0
      bulkProgress.message = 'Starting bulk generation...'

      try {
        const response = await fetch(props.generateAllUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken()
          },
          body: JSON.stringify({
            cover: bulkOptions.includeCover,
            blocks: bulkOptions.includeBlocks,
            force: bulkOptions.forceRegenerate
          })
        })

        const data = await response.json()

        if (data.success) {
          // Count generated images
          let generatedCount = 0
          let totalCost = 0

          // Update cover image if generated
          if (data.cover && !data.cover.skipped) {
            generatedCount += 1
            totalCost += data.cover.cost_cents || 0
          }

          // Update block images
          if (data.blocks && Array.isArray(data.blocks)) {
            data.blocks.forEach((blockResult) => {
              if (!blockResult.skipped && blockResult.url) {
                const block = blocks.value[blockResult.block_index]
                if (block) {
                  block.image = {
                    url: blockResult.url,
                    prompt: blockResult.prompt,
                    cost_cents: blockResult.cost_cents,
                    generated_at: new Date().toISOString()
                  }
                  generatedCount += 1
                  totalCost += blockResult.cost_cents || 0
                }
              }
            })
            emitUpdate()
          }

          const costDollars = (totalCost / 100).toFixed(2)
          bulkResult.value = {
            success: true,
            message: `Generated ${generatedCount} image${generatedCount !== 1 ? 's' : ''} ($${costDollars})`,
            errors: data.errors || []
          }
        } else {
          bulkResult.value = {
            success: false,
            message: data.message || data.error || 'Generation failed',
            errors: []
          }
        }
      } catch (error) {
        bulkResult.value = {
          success: false,
          message: error.message,
          errors: []
        }
      } finally {
        bulkGenerating.value = false
        bulkProgress.percent = 100
        bulkProgress.message = 'Complete'
      }
    }

    return {
      blocks,
      showAddMenu,
      showBulkModal,
      blockTypes,
      getBlockTypeLabel,
      emitUpdate,
      addBlock,
      removeBlock,
      moveBlock,
      addListItem,
      removeListItem,
      addTableColumn,
      addTableRow,
      removeTableRow,
      onDragStart,
      onDragOver,
      onDrop,
      // Image handling
      blockGenerating,
      blockStatus,
      generateBlockImage,
      uploadBlockImage,
      removeBlockImage,
      // Bulk generation
      bulkOptions,
      bulkGenerating,
      bulkProgress,
      bulkResult,
      blocksWithoutImages,
      estimatedCost,
      closeBulkModal,
      runBulkGeneration
    }
  }
}
</script>

<style scoped>
.content-blocks-editor {
  min-height: 200px;
}

[draggable="true"] {
  cursor: grab;
}

[draggable="true"]:active {
  cursor: grabbing;
}
</style>
